import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Send, Mail, History } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "@/lib/adminApi";
import { Field, inputCls, btnPrimary, btnGhost } from "./ui";

const EMPTY = { subject: "", headline: "", body: "", cta_text: "", cta_url: "" };

export default function BroadcastPanel() {
  const [form, setForm] = useState(EMPTY);
  const [count, setCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);

  const load = () => {
    adminApi.get("/subscribers").then((r) => setCount(r.count || 0)).catch(() => {});
    adminApi.get("/broadcasts").then((r) => setHistory(r.broadcasts || [])).catch(() => {});
  };

  useEffect(load, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (path, label) => {
    if (!form.subject.trim() || !form.body.trim()) {
      return toast.error("Subject and body are required.");
    }
    setBusy(label);
    try {
      const res = await adminApi.post(path, form);
      if (path === "/broadcast/test") {
        toast.success(`Test sent to ${res.to}. Check your inbox.`);
      } else {
        toast.success(`Sent to ${res.broadcast.sent} of ${res.broadcast.recipients} subscribers.`);
        setForm(EMPTY);
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
      setConfirming(false);
    }
  };

  return (
    <div data-testid="admin-broadcast-panel" className="grid gap-10 lg:grid-cols-[1.25fr_0.75fr]">
      <div>
        <p className="text-sm leading-relaxed text-latte">
          Write it once, send it to the whole list. Everything goes out in The Shi
          Sheet&rsquo;s brown-and-gold template &mdash; kicker, italic headline, your words,
          one button.
        </p>

        <div className="mt-8 space-y-5 rounded-2xl border border-gold/30 bg-cocoa p-6 md:p-8">
          <Field label="Subject line (what lands in the inbox)">
            <input
              data-testid="broadcast-subject-input"
              className={inputCls}
              placeholder="This week on The Shi Sheet"
              value={form.subject}
              onChange={set("subject")}
            />
          </Field>
          <Field label="Headline (big italic line inside the email — defaults to subject)">
            <input
              data-testid="broadcast-headline-input"
              className={inputCls}
              placeholder="Let's get into it."
              value={form.headline}
              onChange={set("headline")}
            />
          </Field>
          <Field label="Body — blank line starts a new paragraph">
            <textarea
              data-testid="broadcast-body-input"
              rows={10}
              className={inputCls}
              placeholder={"Honey, this week was a lot...\n\nAnd that's what Shi said."}
              value={form.body}
              onChange={set("body")}
            />
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Button label (optional)">
              <input
                data-testid="broadcast-cta-text-input"
                className={inputCls}
                placeholder="Watch this week's episode"
                value={form.cta_text}
                onChange={set("cta_text")}
              />
            </Field>
            <Field label="Button link (must start with https://)">
              <input
                data-testid="broadcast-cta-url-input"
                className={inputCls}
                placeholder="https://…"
                value={form.cta_url}
                onChange={set("cta_url")}
              />
            </Field>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              data-testid="broadcast-test-button"
              disabled={!!busy}
              onClick={() => submit("/broadcast/test", "test")}
              className={btnGhost}
            >
              <span className="inline-flex items-center gap-2">
                <Mail size={15} /> {busy === "test" ? "Sending…" : "Send test to me"}
              </span>
            </button>
            {confirming ? (
              <>
                <button
                  data-testid="broadcast-confirm-button"
                  disabled={!!busy}
                  onClick={() => submit("/broadcast", "blast")}
                  className={btnPrimary}
                >
                  {busy === "blast" ? "Sending the sheet…" : `Yes — send to ${count}`}
                </button>
                <button onClick={() => setConfirming(false)} className={btnGhost}>
                  Cancel
                </button>
              </>
            ) : (
              <button
                data-testid="broadcast-send-button"
                disabled={!!busy || count === 0}
                onClick={() => setConfirming(true)}
                className={btnPrimary}
              >
                <span className="inline-flex items-center gap-2">
                  <Send size={15} /> Send to all {count} subscribers
                </span>
              </button>
            )}
          </div>
          {count === 0 && (
            <p className="text-xs text-latte">
              No subscribers on the list yet &mdash; the blast unlocks once someone signs up.
            </p>
          )}
        </div>
      </div>

      <div>
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-gold">
          <History size={14} /> Sent history
        </p>
        <div data-testid="broadcast-history" className="mt-5 space-y-3">
          {history.length === 0 && (
            <p className="text-sm text-latte">Nothing sent yet.</p>
          )}
          {history.map((b) => (
            <div
              key={b.id}
              data-testid={`broadcast-history-${b.id}`}
              className="rounded-2xl border border-corduroy bg-cocoa/60 p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-cream">{b.subject}</p>
                {b.kind === "premiere" && (
                  <span className="shrink-0 rounded-full bg-forest/40 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-gold">
                    Auto
                  </span>
                )}
              </div>
              <p className="mt-2 text-xs text-latte">
                {b.sent_at ? format(new Date(b.sent_at), "MMM d, yyyy · h:mm a") : ""}
              </p>
              <p className="mt-1 text-xs text-latte">
                <span className="text-gold">{b.sent}</span> delivered
                {b.failed ? ` · ${b.failed} failed` : ""} of {b.recipients}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
